import { useEffect, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'

import { useI18n } from 'builder/hooks/useI18n'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'

import { TRANSLATION } from '../../../constants'
import { Service } from '../../../../types'

export type First90DaysPlanInfo = {
  badge?: string
  header: string
  subHeader: string
  book_coach: string
  close_modal: string
  actionText: string
  actions: {
    book_coach: string
    see_plan: string
  }
}

type Step = {
  completed?: boolean
  target_score?: number
}

export type Steps = {
  page_seen?: Step
  career_coaching?: Step
}

type First90DaysPlanConfig = {
  text: First90DaysPlanInfo
  status: 'initial' | 'completed' | 'progress'
  steps: Steps
  handleCloseModal: () => void
}

const localStorageKey = 'first_90_days_seen_modal'

export const useFirst90DaysPlanConfig = (service: Service): First90DaysPlanConfig => {
  const { i18n } = useI18n()
  const { features } = useFeaturesConfig()
  const [isModalOpen, setIsModalOpen] = useState(
    !localStorage.getItem(localStorageKey) && features.careerCoaching,
  )
  const percentageProgress = service.calculatedProgress?.percentageProgress || 0

  const getStep = (stepName: string) => {
    return service.data?.steps?.find(step => step.step_name === stepName)
  }

  const getSteps = () => {
    return {
      page_seen: getStep('page_seen'),
      career_coaching: getStep('career_coaching'),
    }
  }

  const getStatus = () => {
    if (isModalOpen) return 'initial'
    if (percentageProgress >= 100) return 'completed'
    return 'progress'
  }

  const getText = () => {
    const status = getStatus()
    const pageSeen = getStep('page_seen')
    const careerCoaching = getStep('career_coaching')
    const key = `first_90_days_plan.${status}`

    const getKey = () => {
      if (pageSeen) return 'career_coaching'
      return 'page_seen'
    }

    const getActionKeys = () => {
      const pageSeenKey = pageSeen?.completed ? 'completed' : 'not_completed'
      const careerCoachingKey = careerCoaching?.completed ? 'completed' : 'not_completed'

      return {
        see_plan: i18n.t(`${TRANSLATION}.${key}.actions.page_seen.${pageSeenKey}`),
        book_coach: i18n.t(`${TRANSLATION}.${key}.actions.career_coaching.${careerCoachingKey}`),
      }
    }

    if (status === 'progress') {
      return {
        ...(i18n.t(`${TRANSLATION}.${key}`) as unknown as First90DaysPlanInfo),
        header: i18n.t(`${TRANSLATION}.${key}.header.${getKey()}`),
        subHeader: i18n.t(`${TRANSLATION}.${key}.subHeader.${getKey()}`),
        actions: getActionKeys(),
      }
    }

    return i18n.t(`${TRANSLATION}.${key}`) as unknown as First90DaysPlanInfo
  }

  const handleCloseModal = () => {
    localStorage.setItem(localStorageKey, 'true')
    setIsModalOpen(false)
    trackInternalEvent('progress_promo_close', { type: 'coaching' })
  }

  useEffect(() => {
    if (isModalOpen) {
      trackInternalEvent('progress_promo', { type: 'coaching' })
    }
  }, [isModalOpen])

  return {
    text: getText(),
    steps: getSteps(),
    status: getStatus(),
    handleCloseModal,
  }
}
